import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardHeader, CardBody, Alert, Spinner } from "reactstrap";
import Widgets from "./Widgets";
import { ThreatTypeChart } from "./ThreatTypeChart";
import { MonthlyThreadChart } from "./MonthlyThreadChart";
import SeverityThreads from "./SeverityThreads";
import CriticalAlerts from "./CriticalAlerts";
import SeverityBreakdownChart from "./SeverityBreakdownChart";
import { postureAlertIconChipStyle, postureAlertIconStyle, postureIconChipStyle, postureIconStyle } from "./iconStyles";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const cache = { data: null, ts: 0 };
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const PostureSummary = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const authUser = JSON.parse(sessionStorage.getItem("authUser"));
    return {
      "Content-Type": "application/json",
      ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
    };
  };
  
  useEffect(() => {
    const fetchData = async () => {
      if (cache.data && Date.now() - cache.ts < CACHE_TTL) {
        setStats(cache.data.stats);
        setAnalytics(cache.data.analytics);
        setLoading(false);
        return;
      }

      try {
        // Fetch both stats and analytics in parallel
        const [statsResponse, analyticsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/threats/stats`, {
            headers: getAuthHeaders(),
          }),
          fetch(`${API_BASE_URL}/threats/analytics`, {
            headers: getAuthHeaders(),
          })
        ]);

        // Check for authentication errors
        if (statsResponse.status === 401 || analyticsResponse.status === 401) {
          setError("Session expired. Please login again.");
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
          return;
        }

        const statsData = await statsResponse.json();
        const analyticsData = await analyticsResponse.json();

        if (statsData.success) {
          setStats(statsData.stats);
        }

        if (analyticsData.success) {
          setAnalytics(analyticsData.analytics);
        }

        cache.data = {
          stats: statsData.success ? statsData.stats : null,
          analytics: analyticsData.success ? analyticsData.analytics : null,
        };
        cache.ts = Date.now();
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);
  
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <div className="text-center">
              <Spinner style={{ color: ORANGE_COLORS.primary, width: "3rem", height: "3rem" }} />
              <p className="mt-3 text-muted">Loading security posture data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <style>{`
            .posture-page-title {
              color: var(--vz-body-color) !important;
            }
            .posture-page-icon {
              width: 32px;
              height: 32px;
              border-radius: 8px;
              border: 1px solid var(--vz-border-color);
              background: rgba(var(--vz-primary-rgb), 0.08);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .posture-page-icon i {
              color: var(--vz-primary) !important;
              font-size: 16px;
            }
          `}</style>
          {/* Page Title */}
          <Row className="mb-4">
            <Col>
            <h4 className="mb-0 posture-page-title">
              <div className="posture-page-icon me-2">
                 <i className="ri-shield-line"></i>
              </div>
            Security Posture Summary
            </h4>
            <p className="text-muted mb-0 mt-2" style={{ fontSize: "13px" }}>
               An overview of your current security metrics and trends
            </p>
          </Col>


          </Row>
          
          {error && (
            <Alert 
              color="danger" 
              className="mb-3"
              style={{
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                border: '1px solid rgba(220, 53, 69, 0.3)',
                color: '#dc3545'
              }}
            >
              <span className="me-2" style={postureAlertIconChipStyle("#dc3545")}>
                <i className="ri-error-warning-line" style={postureAlertIconStyle("#dc3545")}></i>
              </span>
              {error}
            </Alert>
          )}
          
          <Row className="mb-4">
            <Widgets stats={stats} />
          </Row>
          
          <Row className="mb-4">
            <Col xl={6}>
              <Card
                style={{ 
                  background: "var(--vz-secondary-bg, #fff)",
                  border: "1px solid var(--vz-border-color)",
                  borderRadius: "10px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  overflow: "hidden"
                }}
              >
                <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
                  <div className="d-flex align-items-center">
                    <div className="me-3" style={postureIconChipStyle("#FF5B00")}>
                      <i className="ri-pie-chart-line" style={postureIconStyle("#FF5B00")}></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0" style={{ fontSize: '16px' }}>Threat Type Distribution</h5>
                      <small className="text-muted">Breakdown by threat category</small>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {analytics?.threat_types && (
                    <ThreatTypeChart
                      analyticsData={analytics.threat_types}
                      dataColors='["#FF5B00", "#e85a28", "#d14d1f", "#ff8c61", "#b33a15", "#ffb399", "#c94219"]'
                   totalThreatsFromStats={parseInt(stats.total_threats, 10)}
                      />
                  )}
                </CardBody>
              </Card>
            </Col>    
            <Col xl={6}>
              <Card
                style={{ 
                  background: "var(--vz-secondary-bg, #fff)",
                  border: "1px solid var(--vz-border-color)",
                  borderRadius: "10px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  overflow: "hidden"
                }}
              >
                <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
                  <div className="d-flex align-items-center">
                    <div className="me-3" style={postureIconChipStyle("#FF5B00")}>
                      <i className="ri-line-chart-line" style={postureIconStyle("#FF5B00")}></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0" style={{ fontSize: '16px' }}>Monthly Threat Trends</h5>
                      <small className="text-muted">Historical threat analysis</small>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {analytics?.monthly_trend && (
                    <MonthlyThreadChart 
                      dataColors={`["${ORANGE_COLORS.primary}"]`}
                      analyticsData={analytics.monthly_trend}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col xl={6}>
              {analytics?.critical_threats && (
                <CriticalAlerts threatData={analytics.critical_threats} />
              )}
            </Col>
            <Col xl={6}>
              <Card
                style={{ 
                  background: "var(--vz-secondary-bg, #fff)",
                  border: "1px solid var(--vz-border-color)",
                  borderRadius: "10px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  overflow: "hidden"
                }}
              >
                <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
                  <div className="d-flex align-items-center">
                    <div className="me-3" style={postureIconChipStyle("#FF5B00")}>
                      <i className="ri-error-warning-line" style={postureIconStyle("#FF5B00")}></i>
                    </div>
                    <div>
                      <h5 className="card-title mb-0" style={{ fontSize: '16px' }}>Severity Breakdown</h5>
                      <small className="text-muted">Distribution of alerts by severity</small>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {analytics?.severity_breakdown && (
                    <SeverityBreakdownChart analyticsData={analytics.severity_breakdown} />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          
          <Row>
            {analytics?.critical_threats && (
              <SeverityThreads threatData={analytics.critical_threats} />
            )}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PostureSummary;
